import React from "react";
import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { ArrowRight } from "lucide-react";

const LIFESTYLE_1 = "/lifestyle/lux-leadership.png";
const LIFESTYLE_2 = "/lifestyle/lux-nightlife.png";

const About = () => {
  return (
    <div className="bg-[#050505] text-white">
      <section
        className="relative grain min-h-[70vh] flex items-end"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0.4) 0%, rgba(5,5,5,0.85) 80%, #050505 100%), url(${LIFESTYLE_1})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 w-full">
          <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
            THE BRAND
          </div>
          <h1
            className="font-display uppercase leading-[0.85] tracking-tight max-w-4xl"
            style={{ fontSize: "clamp(3rem, 9vw, 8rem)" }}
          >
            Built for <br /> <span className="text-stroke">the modern</span> alpha.
          </h1>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
            ORIGIN
          </div>
          <h2
            className="font-display uppercase leading-[0.9]"
            style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)" }}
          >
            The natural <br /> selection.
          </h2>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <div className="space-y-6 text-white/75 leading-relaxed font-body text-lg">
            <p>
              Primenax was founded on a single principle: men deserve
              supplements that work — without shortcuts, without synthetics,
              without compromise.
            </p>
            <p>
              We exist for the man who refuses to coast. The man who trains
              hard, eats clean, leads his family, and demands more from his
              body at 40 than he did at 25. Our formulas are engineered around
              the science of male physiology — clinically-studied botanicals,
              adaptogens, and minerals dosed at levels that actually do
              something.
            </p>
            <p>
              Every batch is third-party tested. Every ingredient is justified.
              Every bottle is a promise: be the natural selection.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="relative grain border-y border-white/10">
        <div
          className="h-[60vh] min-h-[420px]"
          style={{
            backgroundImage: `linear-gradient(90deg, #050505 0%, rgba(5,5,5,0.4) 60%, rgba(5,5,5,0.1) 100%), url(${LIFESTYLE_2})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-full flex items-center">
            <div className="max-w-xl">
              <Reveal>
                <h2
                  className="font-display uppercase leading-[0.9]"
                  style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)" }}
                >
                  No filler. <br /> No hype. <br /> Just signal.
                </h2>
                <p className="mt-6 text-white/70 max-w-md">
                  We don't believe in proprietary blends or 12-syllable mystery
                  ingredients. Every dose is disclosed. Every batch is verified.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section
        className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32"
        data-testid="manifesto-section"
      >
        <Reveal>
          <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
            THE CODE
          </div>
          <h2
            className="font-display uppercase leading-[0.85]"
            style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)" }}
          >
            We don't sell <br /> <span className="text-stroke">supplements.</span>
          </h2>
          <p className="mt-8 max-w-2xl text-white/70 leading-relaxed text-lg">
            We sell a standard. Primenax was built for the man who refuses to
            coast — who trains hard, leads sharp, shows up on time, and still
            wants more from his body at 45 than he had at 25. That man doesn't
            need motivation. He needs a protocol that respects his intelligence.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
          {[
            {
              n: "01",
              t: "Identity, not aspiration.",
              d: "We don't help you become someone else. We help you finish the man you've already started building.",
            },
            {
              n: "02",
              t: "Evidence, not hype.",
              d: "Clinically-recognized doses. Open ingredient lists. No proprietary smokescreens. The bottle is a contract.",
            },
            {
              n: "03",
              t: "Standard, not subscription.",
              d: "Primenax is a daily standard you set for yourself. Drive, focus, recovery, presence — every morning.",
            },
          ].map((p, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="bg-[#0A0A0A] p-10 h-full">
                <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
                  {p.n}
                </div>
                <h3 className="font-display text-3xl tracking-tight uppercase leading-tight">
                  {p.t}
                </h3>
                <p className="text-white/65 mt-4 leading-relaxed">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
        {[
          { n: "01", t: "Naturally Formulated", d: "Plant-based actives, vitamins, and minerals. Nothing synthetic." },
          { n: "02", t: "Clinically-Backed Doses", d: "Ingredients dosed at levels supported by human research." },
          { n: "03", t: "Third-Party Tested", d: "Independent verification of identity, potency, and purity." },
        ].map((p, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="bg-[#0A0A0A] p-10 h-full">
              <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
                {p.n}
              </div>
              <h3 className="font-display text-2xl tracking-tight uppercase">
                {p.t}
              </h3>
              <p className="text-white/60 mt-3 leading-relaxed">{p.d}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24">
        <div className="bg-[#0A0A0A] border border-white/10 p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-3">
              READY?
            </div>
            <h3
              className="font-display uppercase leading-[0.9]"
              style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}
            >
              Reclaim your prime.
            </h3>
          </div>
          <Link
            to="/shop"
            data-testid="about-shop-cta"
            className="inline-flex items-center gap-3 bg-[#D10000] hover:bg-[#A30000] text-white font-display text-lg tracking-widest uppercase px-8 py-4 transition-colors"
          >
            Shop Primenax
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
